import { AxisCode, AxisKind, ClassifiedObjectType, ValueType } from '@atlas/types'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { DISCOVER_FILES_KEY, invalidateClassifiedReads, useAxes } from '@/hooks/useClassification'
import { apiErrorMessage } from '@/lib/api-error'
import { notification } from '@/notifications/notification-store'
import { classificationApi } from '@/services/classification.service'
import type { FileWithTagsDTO } from '@/services/file.service'
import { fileService } from '@/services/file.service'

/** One persisted generic `object_attribute` value, ready to render as an Attributes-panel row. */
export interface GenericAttributeRow {
	axisCode: string
	label: string
	value: string
}

/**
 * Attribute axes the generic Attributes list must not render.
 *
 * Two reasons, deliberately kept in one list because the effect is the same —
 * the row would be noise:
 *
 * - **Already shown elsewhere.** Licence, licence link and approval status have
 *   the panel's own fixed rows, so a generic row would duplicate them.
 * - **Internal bookkeeping.** `deliverable_order` is a positional integer the
 *   work-order flow stamps to sequence deliverables; it means nothing to someone
 *   reading an asset's attributes, and showing it invites editing a value the
 *   ordering depends on.
 *
 * A deny-list, not an allow-list, on purpose: attribute axes are DATA and grow by
 * INSERT, so a new one must appear here without a deploy. That is only safe while
 * the exclusions stay few and justified — an axis is added here because it is
 * genuinely not user-facing, never merely to tidy the panel.
 */
const FIXED_ROW_AXIS_CODES: string[] = [AxisCode.LICENSE, AxisCode.DELIVERABLE_ORDER]

/** Root of the file-with-tags key family. Exported so the realtime query bridge invalidates the whole family on reconnect. */
export const FILE_WITH_TAGS_KEY = 'file-with-tags'

/** Query key for one file with its classification tags. Exported so the realtime query bridge invalidates the same key. */
export const fileWithTagsKey = (fileId: string | null) => [FILE_WITH_TAGS_KEY, fileId]

/**
 * Resolve one file (with its classification tags) for the asset-detail page.
 * Calls `GET /files/{id}/with-tags` directly — a real, dedicated endpoint, not a
 * `discover({})` scan capped at 100 rows. A file no longer silently renders as
 * "not found" just for sitting past the first page of the shared library.
 */
export function useAssetDetail(fileId: string | null): { file: FileWithTagsDTO | null; isLoading: boolean } {
	const { data: file = null, isLoading } = useQuery({
		queryKey: fileWithTagsKey(fileId),
		queryFn: () => fileService.getFileWithTags(fileId as string),
		enabled: !!fileId,
	})
	return { file, isLoading }
}

/**
 * Persisted generic `object_attribute` values for the asset-detail Attributes panel —
 * every active `kind='attribute'` axis (not already covered by the panel's fixed
 * licence/licence-link/lifecycle rows) that carries a non-empty value for this file.
 * Registry-driven (label + inclusion come from the axis itself), so no attribute axis
 * code is special-cased here. A fetch failure surfaces as `data: undefined` rather
 * than throwing, so the asset-detail page still renders.
 *
 * The raw-value fetch (`getObjectAttributes`) is a separate query from label
 * resolution (axis name + `list`-typed value labels via `listValues`), so a registry
 * edit — an axis renamed, or a value's label changed — is reflected on next render
 * without needing to invalidate or refetch the raw attribute codes; only the label
 * lookups, driven by `useAxes`/`useQueries` and already reactive, need to resolve.
 * Axis codes are sorted before use in the query key and the request itself, so a
 * registry re-order (not a content change) can never produce a distinct cache entry
 * or an extra request.
 */
export function useFileGenericAttributes(fileId: string | null) {
	const { data: axes = [] } = useAxes()
	const attributeAxes = axes
		.filter(
			(axis) =>
				axis.status === 'active' && axis.kind === AxisKind.ATTRIBUTE && !FIXED_ROW_AXIS_CODES.includes(axis.code),
		)
		.sort((a, b) => a.code.localeCompare(b.code))
	const axisCodes = attributeAxes.map((axis) => axis.code)

	const listAxes = attributeAxes.filter((axis) => axis.valueType === ValueType.LIST)
	const valueQueries = useQueries({
		queries: listAxes.map((axis) => ({
			queryKey: ['values', axis.axisId],
			queryFn: () => classificationApi.listValues(axis.axisId),
		})),
	})
	const labelsByAxisCode = new Map(
		listAxes.map((axis, i) => [axis.code, new Map((valueQueries[i]?.data ?? []).map((v) => [v.code, v.label]))]),
	)

	const rawAttributes = useQuery({
		queryKey: ['file-generic-attributes', fileId, axisCodes],
		queryFn: async (): Promise<Record<string, string[]>> => {
			const [obj] = await classificationApi.getObjectAttributes(
				ClassifiedObjectType.FILE,
				[fileId as string],
				axisCodes,
			)
			return obj?.attributes ?? {}
		},
		enabled: !!fileId && axisCodes.length > 0,
	})

	const data: GenericAttributeRow[] | undefined = rawAttributes.data
		? attributeAxes
				.map((axis) => {
					const codes = rawAttributes.data?.[axis.code] ?? []
					const values =
						axis.valueType === ValueType.LIST
							? codes.map((code) => labelsByAxisCode.get(axis.code)?.get(code) ?? code)
							: codes
					return { axisCode: axis.code, label: axis.name, value: values.join(', ') }
				})
				.filter((row) => row.value.length > 0)
		: undefined

	return { ...rawAttributes, data }
}

/**
 * Fetch a fresh pre-signed download URL and open it in a new tab (the URL is the
 * credential — the browser handles the actual transfer, not this app). A fresh URL
 * is requested on every download rather than reusing one from an earlier fetch, since
 * it's short-lived. Reuses the same `fileService.getDownloadUrl` the work-order file
 * manager already calls (`useWorkOrderFiles.ts`) — no new backend endpoint.
 */
export function useDownloadFile() {
	return useMutation({
		mutationFn: (fileId: string) => fileService.getDownloadUrl(fileId),
		onSuccess: (downloadUrl) => {
			window.open(downloadUrl, '_blank', 'noopener,noreferrer')
		},
	})
}

/**
 * Delete a file from the Library — the asset and its classification together.
 *
 * One call does both: `DELETE /files/{id}` soft-deletes the file row, moves the S3 object
 * aside, and soft-deletes the file's classification anchor. So there is no second request
 * to make here, and no ordering to get wrong.
 *
 * Soft on both sides, deliberately: the anchor's `deleted_at` is what drops the file out of
 * search, facets, resolved context and the brand/product pages, which is what "its
 * classification is gone" means to a reader. The `classification` tuple rows themselves stay
 * for audit/provenance and are unreachable once the anchor is gone.
 *
 * Optimistic on the discover lists the Library grid reads, because the grid is the surface
 * the click happens on and re-running discovery takes seconds. The detail page has nothing
 * to patch - the asset it is about is gone - so it navigates away instead, which is the
 * caller's decision rather than this hook's.
 *
 * `invalidateClassifiedReads` rather than one key: a deleted file leaves brand pages,
 * product pages and facet counts too, and those are not enumerable from here.
 */
export function useDeleteFile() {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (fileId: string) => fileService.deleteFile(fileId),

		onMutate: async (fileId: string) => {
			await qc.cancelQueries({ queryKey: [DISCOVER_FILES_KEY] })
			const discoverEntries = qc.getQueriesData<FileWithTagsDTO[]>({ queryKey: [DISCOVER_FILES_KEY] })
			// Captured before the row goes, so the notification names the file the user
			// recognises rather than the uuid they never saw.
			const fileName =
				discoverEntries.flatMap(([, files]) => files ?? []).find((file) => file.fileId === fileId)?.fileName ?? fileId

			qc.setQueriesData<FileWithTagsDTO[]>({ queryKey: [DISCOVER_FILES_KEY] }, (files) =>
				files ? files.filter((file) => file.fileId !== fileId) : files,
			)
			return { discoverEntries, fileName }
		},

		// Unconditional: any failure means the file still exists, so every list goes back.
		onError: (error, fileId, context) => {
			for (const [queryKey, data] of context?.discoverEntries ?? []) qc.setQueryData(queryKey, data)
			notification.error(apiErrorMessage(error, `Could not delete ${context?.fileName ?? fileId}.`))
		},

		onSuccess: (_data, _fileId, context) => {
			notification.success(`Deleted ${context?.fileName}, with its classification.`)
		},

		onSettled: () => invalidateClassifiedReads(qc),
	})
}
